import { useState, useEffect, useRef } from 'react';
import { auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from '../components/ConfirmModal';
import {
  FaWrench, FaPlus, FaTimes, FaCalendarAlt, FaFlag, FaFileAlt, FaChevronRight,
  FaTint, FaBolt, FaThermometerHalf, FaCog, FaBuilding, FaBug, FaQuestionCircle,
  FaMinus, FaCircle, FaExclamationTriangle, FaClock, FaCheckCircle,
  FaImages, FaTools, FaPhone, FaExpand, FaKey,
} from 'react-icons/fa';

import TenantSidebar from './TenantSidebar';
import TenantNavbar from './TenantNavbar';
import LoadingScreen from '../PropertyManager/LoadingScreen';
import '../PropertyManager/Sidebar.css';
import '../PropertyManager/Navbar.css';
import './TenantMaintenance.css';
import Tutorial from '../PropertyManager/Tutorial';
import { showToast } from '../components/toast';

const BASE_URL = 'https://api.rside.app';

const TM_STEPS = [
  {
    title: 'Maintenance Requests',
    description: 'Submit and track all your maintenance requests from this page.',
  },
  {
    title: 'Filter by Status',
    description: 'Use these tabs to filter your requests — All, Open, In Progress, or Completed.',
    anchorId: 'tm-tabs',
    cardAlignment: 'bottom',
  },
  {
    title: 'Your Requests',
    description: 'Each card shows a request\'s status, priority, and category. Tap to view more details.',
    anchorId: 'tm-list',
    cardAlignment: 'top',
  },
  {
    title: 'New Request',
    description: 'Tap the + button to submit a new maintenance request in just a few steps.',
    anchorId: 'tm-fab',
    cardAlignment: 'top',
  },
];

const CATEGORIES = [
  { id: 'Plumbing',   Icon: FaTint,            color: '#3b82f6' },
  { id: 'Electrical', Icon: FaBolt,            color: '#f97316' },
  { id: 'HVAC',       Icon: FaThermometerHalf, color: '#06b6d4' },
  { id: 'Appliance',  Icon: FaCog,             color: '#a855f7' },
  { id: 'Structural', Icon: FaBuilding,        color: '#d97706' },
  { id: 'Pest',       Icon: FaBug,             color: '#22c55e' },
  { id: 'Other',      Icon: FaQuestionCircle,  color: '#9ca3af' },
];

const URGENCIES = [
  { id: 'Low',    sub: 'Can wait a few days',   Icon: FaMinus },
  { id: 'Normal', sub: 'Needs attention soon',  Icon: FaCircle },
  { id: 'Urgent', sub: 'Immediate attention',   Icon: FaExclamationTriangle },
];

const STEP_LABELS = ['', 'What\'s the issue?', 'Describe it', 'Final details'];

const EMPTY_NEW = { category: '', title: '', description: '', urgency: 'Normal', okToEnter: false };

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return '';
  const diff = Math.floor((Date.now() - d) / 1000);
  if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))} minutes ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
  const days = Math.floor(diff / 86400);
  return days === 1 ? '1 day ago' : `${days} days ago`;
}

function mapPriority(p) {
  if (p === 3 || p === '3') return 'Urgent';
  if (p === 2 || p === '2') return 'Medium';
  return 'Low';
}

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return isNaN(d) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDateTime(dateStr) {
  if (!dateStr) return '—';
  const normalized = typeof dateStr === 'string' && !/[Zz]|[+-]\d{2}:\d{2}/.test(dateStr) ? dateStr + 'Z' : dateStr;
  const d = new Date(normalized);
  if (isNaN(d)) return dateStr;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const STATUS_CLS = { 'open': 'tm-st-open', 'in progress': 'tm-st-inprog', 'completed': 'tm-st-done' };
const PRI_CLS    = { 'Urgent': 'tm-pri-urgent', 'Medium': 'tm-pri-medium', 'Low': 'tm-pri-low', 'Normal': 'tm-pri-medium' };
const BAR_CLS    = { 'open': 'tm-bar-open', 'in progress': 'tm-bar-inprog', 'completed': 'tm-bar-done' };

const TABS = ['All', 'Open', 'In Progress', 'Completed'];

function TenantMaintenance() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tenantName, setTenantName] = useState('');
  const [initials, setInitials] = useState('');
  const [workOrders, setWorkOrders] = useState([]);
  const [filter, setFilter] = useState('All');
  const [confirmDialog, setConfirmDialog] = useState(null);
  const askConfirm = (opts, onConfirm) => setConfirmDialog({ ...opts, onConfirm });
  const [selected, setSelected] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [step, setStep] = useState(1);
  const [newWO, setNewWO] = useState(EMPTY_NEW);
  const [newWoPhotos, setNewWoPhotos] = useState([]);
  const newWoPhotoInputRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [closeRequesting, setCloseRequesting] = useState(false);
  const [doorId, setDoorId] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [pmEmail, setPmEmail] = useState('');
  const [pmName, setPmName] = useState('');
  const [woPhotos, setWoPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [woVendors, setWoVendors] = useState([]);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [entryResponding, setEntryResponding] = useState(false);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) { navigate('/tenant/login'); return; }
      try {
        const sessionRes = await fetch(`${BASE_URL}/TenantApp/SessionInfo?email=${encodeURIComponent(user.email)}`);
        const session = await sessionRes.json();
        const name = session?.tenant?.full_name || '';
        setTenantName(name);
        setInitials(name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2));
        setUserEmail(user.email);
        const pmE = session?.property_manager?.email || '';
        const pmN = session?.property_manager?.full_name || session?.property_manager?.name || '';
        setPmEmail(pmE);
        setPmName(pmN);
        const did = session?.tenant?.door_id;
        setDoorId(did);
        if (did) await loadWorkOrders(did);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    });
    return unsub;
  }, [navigate]);

  const selectedId = selected?.id ?? null;
  // Always read entry_request from the live workOrders list so the first-open
  // fetch (GetWorkOrderDetails → setWorkOrders) is reflected immediately.
  const liveSelected = selectedId
    ? (workOrders.find(w => String(w.id) === String(selectedId)) ?? selected)
    : null;

  useEffect(() => {
    if (!selectedId) {
      setTimeline([]);
      setWoPhotos([]);
      setWoVendors([]);
      return;
    }
    const woId = selectedId;
    let cancelled = false;

    // Fetch fresh details (entry_request + close_requested fields not in list endpoint)
    fetch(`${BASE_URL}/GetWorkOrderDetails?work_order_id=${encodeURIComponent(woId)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (cancelled || !d) return;
        const wo = d.work_order || d;
        const status       = wo.entry_request_status || wo.Entry_Request_Status || wo.EntryRequestStatus || '';
        const date         = wo.entry_request_date   || wo.Entry_Request_Date   || wo.EntryRequestDate   || '';
        const closeReq     = !!(wo.RequestToDelete || wo.close_requested || wo.Close_Requested || wo.request_to_delete);
        setWorkOrders(prev => prev.map(w => {
          if (String(w.id) !== String(woId)) return w;
          const localStatus = w.entry_request_status || '';
          const resolved = localStatus === 'Approved' || localStatus === 'Denied';
          const finalStatus = resolved && status === 'Pending' ? localStatus : (status || localStatus);
          // Only update close_requested from API if we haven't already set it locally
          const finalClose = w.close_requested || closeReq;
          return { ...w, entry_request_status: finalStatus, entry_request_date: date, close_requested: finalClose };
        }));
      })
      .catch(() => {});

    // Timeline
    setTimelineLoading(true);
    fetch(`${BASE_URL}/GetWorkOrderTimeline?work_order_id=${encodeURIComponent(woId)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled && d) setTimeline(d.timeline || d.Timeline || (Array.isArray(d) ? d : [])); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setTimelineLoading(false); });

    // Assigned vendors
    fetch(`${BASE_URL}/GetWorkOrderServicePro?work_order_id=${encodeURIComponent(woId)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled && d) setWoVendors(d.service_pros || []); })
      .catch(() => {});

    // Photos
    setPhotosLoading(true);
    setWoPhotos([]);
    fetch(`${BASE_URL}/GetWorkOrderImages?work_order_id=${encodeURIComponent(woId)}`)
      .then(r => r.ok ? r.json() : null)
      .then(async (d) => {
        if (cancelled || !d) return;
        const photos = d.photos || [];
        const loaded = await Promise.all(photos.map(async (p) => {
          try {
            const imgRes = await fetch(`${BASE_URL}/GetWorkOrderImageFile?photo_id=${encodeURIComponent(p.photo_id)}`);
            if (!imgRes.ok) return null;
            const blob = await imgRes.blob();
            return { ...p, blobUrl: URL.createObjectURL(blob) };
          } catch { return null; }
        }));
        if (!cancelled) setWoPhotos(loaded.filter(Boolean));
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setPhotosLoading(false); });

    return () => { cancelled = true; };
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadWorkOrders = async (did) => {
    const res = await fetch(`${BASE_URL}/TenantApp/GetWorkOrders?door_id=${did}`);
    const data = await res.json();
    setWorkOrders(
      (data?.work_orders || []).map(wo => ({
        id: wo.id,
        title: wo.Title || 'Untitled',
        status: wo.Stat || 'Open',
        priority: typeof wo.Priority === 'number' || !isNaN(Number(wo.Priority))
          ? mapPriority(wo.Priority)
          : (wo.Priority || 'Medium'),
        category: wo.Category || 'General',
        description: wo.Details || '',
        created: wo.Create_Date || '',
        entry_request_status: wo.entry_request_status || wo.Entry_Request_Status || wo.EntryRequestStatus || '',
        entry_request_date: wo.entry_request_date || wo.Entry_Request_Date || wo.EntryRequestDate || '',
        close_requested: !!(wo.RequestToDelete || wo.close_requested || wo.Close_Requested || wo.request_to_delete),
      }))
    );
  };

  const openNew = () => { setStep(1); setNewWO(EMPTY_NEW); setNewWoPhotos([]); setShowNew(true); };
  const closeNew = () => { setShowNew(false); setStep(1); setNewWO(EMPTY_NEW); setNewWoPhotos([]); };

  const handleSubmitNew = async () => {
    if (!newWO.title.trim() || !doorId) return;
    if (!newWO.description.trim()) { showToast('Please describe the issue before submitting.', 'warning'); return; }
    setSubmitting(true);
    try {
      const priMap = { 'Low': '1', 'Normal': '2', 'Urgent': '3' };
      const pri = priMap[newWO.urgency] || '2';
      const details = newWO.description.trim();
      const formData = new FormData();
      formData.append('title', newWO.title.trim());
      formData.append('priority', pri);
      formData.append('details', details);
      formData.append('door_id', doorId);
      if (newWO.category) formData.append('Category', newWO.category);
      newWoPhotos.forEach((file, i) => formData.append('photos', file, `photo_${i}.jpg`));
      if (newWO.okToEnter) {
        formData.append('entry_request_date', new Date().toISOString().split('T')[0]);
        formData.append('entry_request_status', 'Approved');
      }
      formData.append('CreatedBy', 'Tenant');
      const r = await fetch(`${BASE_URL}/AddWorkOrder`, { method: 'POST', body: formData });
      if (!r.ok) {
        const txt = await r.text().catch(() => '');
        console.error('AddWorkOrder failed:', r.status, txt);
        showToast('Failed to submit your request. Please try again.', 'error');
        return;
      }
      const created = await r.json().catch(() => null);
      const newWoId = created?.id || created?.ID || created?.work_order_id || created?.WorkOrderId || null;
      if (newWoId) {
        fetch(
          `${BASE_URL}/AddWorkOrderTimeline?title=${encodeURIComponent('Work order submitted by tenant')}&updated_by=${encodeURIComponent(tenantName || userEmail || 'Tenant')}&work_order_id=${encodeURIComponent(newWoId)}`,
          { method: 'POST' }
        ).catch(() => {});
      }
      closeNew();
      await loadWorkOrders(doorId);
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEntryResponse = async (status) => {
    if (!selected || entryResponding) return;
    setEntryResponding(true);
    try {
      const r = await fetch(
        `${BASE_URL}/RespondToEntryRequest?work_order_id=${encodeURIComponent(selected.id)}&status=${encodeURIComponent(status)}`,
        { method: 'PUT' }
      );
      if (!r.ok) {
        const txt = await r.text().catch(() => '');
        console.error('RespondToEntryRequest failed:', r.status, txt);
        showToast('Could not save your response. Please try again.', 'error');
        return;
      }
      setSelected(prev => ({ ...prev, entry_request_status: status }));
      setWorkOrders(prev => prev.map(w =>
        String(w.id) === String(selected.id) ? { ...w, entry_request_status: status } : w
      ));
      const tlLabel = status === 'Approved' ? 'Tenant approved entry request' : 'Tenant declined entry request';
      fetch(
        `${BASE_URL}/AddWorkOrderTimeline?title=${encodeURIComponent(tlLabel)}&updated_by=${encodeURIComponent(tenantName || userEmail || 'Tenant')}&work_order_id=${encodeURIComponent(selected.id)}`,
        { method: 'POST' }
      ).catch(() => {});
    } catch (e) {
      console.error(e);
      showToast('Network error. Please try again.', 'error');
    } finally {
      setEntryResponding(false);
    }
  };

  // TODO: Verify cancel endpoint with the backend team before shipping
  const handleRequestClose = async () => {
    if (!selected || closeRequesting) return;
    askConfirm(
      { title: 'Request to Close', message: 'Request to close this work order? Your property manager will be notified.', confirmLabel: 'Send Request', variant: 'warning' },
      async () => {
    setCloseRequesting(true);
    try {
      const r = await fetch(
        `${BASE_URL}/TenantApp/RequestToDeleteWorkOrder?work_order_id=${encodeURIComponent(selected.id)}`,
        { method: 'PUT' }
      );
      if (!r.ok) {
        const txt = await r.text().catch(() => '');
        showToast('Could not send your close request. Please try again.', 'error');
        return;
      }
      // Mark locally so the button updates immediately
      setWorkOrders(prev => prev.map(w =>
        String(w.id) === String(selected.id) ? { ...w, close_requested: true } : w
      ));
    } catch (e) {
      console.error(e);
    } finally {
      setCloseRequesting(false);
    }
      }
    );
  };

  if (loading) return <LoadingScreen />;

  const counts = {
    Open: workOrders.filter(w => w.status.toLowerCase() === 'open').length,
    'In Progress': workOrders.filter(w => w.status.toLowerCase() === 'in progress').length,
    Completed: workOrders.filter(w => w.status.toLowerCase() === 'completed').length,
  };

  const filtered = filter === 'All'
    ? workOrders
    : workOrders.filter(w => w.status.toLowerCase() === filter.toLowerCase());

  const selectedCat = CATEGORIES.find(c => c.id === newWO.category);

  return (
    <div className="home-page">
      <TenantSidebar initials={initials} />
      <div className="main-content">
        <TenantNavbar title="Maintenance" tenantName={tenantName} initials={initials} />

        <div className="tm-body">

          {/* ── Header stats card (like PM WorkOrders) ── */}
          <div className="tm-header-card">
            <div className="tm-header-left">
              <h2 className="tm-header-title">Maintenance Requests</h2>
              <p className="tm-header-sub">Track and submit requests for your unit</p>
            </div>
            <div className="tm-header-stats">
              <div className="tm-sp tm-sp-open">
                <div className="tm-sp-icon"><FaExclamationTriangle /></div>
                <div className="tm-sp-info">
                  <span className="tm-sp-value">{counts.Open}</span>
                  <span className="tm-sp-label">Open</span>
                </div>
              </div>
              <div className="tm-sp tm-sp-inprog">
                <div className="tm-sp-icon"><FaClock /></div>
                <div className="tm-sp-info">
                  <span className="tm-sp-value">{counts['In Progress']}</span>
                  <span className="tm-sp-label">In Progress</span>
                </div>
              </div>
              <div className="tm-sp tm-sp-done">
                <div className="tm-sp-icon"><FaCheckCircle /></div>
                <div className="tm-sp-info">
                  <span className="tm-sp-value">{counts.Completed}</span>
                  <span className="tm-sp-label">Done</span>
                </div>
              </div>
            </div>
          </div>

          {/* Filter tabs */}
          <div id="tm-tabs" className="tm-tabs">
            {TABS.map(t => (
              <button
                key={t}
                className={`tm-tab ${filter === t ? 'tm-tab-active' : ''}`}
                onClick={() => setFilter(t)}
              >
                {t}
                {t !== 'All' && <span className="tm-tab-count">{counts[t]}</span>}
              </button>
            ))}
          </div>

          {/* Work order list */}
          {filtered.length === 0 ? (
            <div className="tm-empty">
              <FaWrench className="tm-empty-icon" />
              <p>No {filter !== 'All' ? filter.toLowerCase() : ''} requests</p>
            </div>
          ) : (
            <div id="tm-list" className="tm-list">
              {filtered.map(wo => {
                const stKey = wo.status.toLowerCase();
                const catItem = CATEGORIES.find(c => c.id === wo.category);
                return (
                  <div key={wo.id} className="tm-card" onClick={() => setSelected(wo)}>
                    <div className={`tm-card-bar ${BAR_CLS[stKey] || 'tm-bar-open'}`} />
                    <div className="tm-card-body">
                      <div className="tm-card-top">
                        <span className={`tm-st-pill ${STATUS_CLS[stKey] || 'tm-st-open'}`}>{wo.status}</span>
                        <span className="tm-time-ago">{timeAgo(wo.created)}</span>
                      </div>
                      <h3 className="tm-card-title">{wo.title}</h3>
                      {wo.description && (
                        <p className="tm-card-desc">{wo.description}</p>
                      )}
                      <div className="tm-card-footer">
                        <div className="tm-card-badges">
                          <span className={`tm-badge ${PRI_CLS[wo.priority] || 'tm-pri-medium'}`}>{wo.priority}</span>
                          {catItem && <span className="tm-badge tm-cat-badge">{wo.category}</span>}
                        </div>
                        <button className="tm-view-btn" onClick={e => { e.stopPropagation(); setSelected(wo); }}>
                          Details <FaChevronRight style={{ fontSize: 9 }} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* FAB */}
        <button id="tm-fab" className="tm-fab" onClick={openNew}>
          <FaPlus />
        </button>

        {/* ── Detail side panel backdrop ── */}
        {selected && <div className="tm-side-backdrop" onClick={() => setSelected(null)} />}

        {/* ── Detail side panel (slides from right) ── */}
        <div className={`tm-side-panel ${selected ? 'tm-side-panel-open' : ''}`}>
          {selected && (() => {
            const selStKey = selected.status.toLowerCase().replace(' ', '-');
            const selCatItem = CATEGORIES.find(c => c.id === selected.category);
            return (
              <>
                {/* Colored hero header */}
                <div className={`tm-panel-hero tm-hero-${selStKey}`}>
                  <div className="tm-panel-hero-inner">
                    <div className="tm-panel-hero-icon">
                      {selCatItem ? <selCatItem.Icon /> : <FaWrench />}
                    </div>
                    <div className="tm-panel-hero-text">
                      <span className="tm-panel-hero-cat">{selected.category}</span>
                      <h2 className="tm-panel-hero-title">{selected.title}</h2>
                    </div>
                  </div>
                  <button className="tm-panel-close" onClick={() => setSelected(null)}>
                    <FaTimes />
                  </button>
                </div>

                {/* Status + priority row */}
                <div className="tm-panel-pill-row">
                  <span className={`tm-st-pill ${STATUS_CLS[selected.status.toLowerCase()] || 'tm-st-open'}`}>{selected.status}</span>
                  <span className={`tm-badge ${PRI_CLS[selected.priority] || 'tm-pri-medium'}`}>{selected.priority}</span>
                </div>

                {/* Scrollable body */}
                <div className="tm-panel-body">

                  {/* Info grid */}
                  <div className="tm-panel-info-grid">
                    <div className="tm-panel-info-item">
                      <span className="tm-panel-info-label">Created</span>
                      <span className="tm-panel-info-val">{fmtDate(selected.created)}</span>
                    </div>
                    <div className="tm-panel-info-item">
                      <span className="tm-panel-info-label">Category</span>
                      <span className="tm-panel-info-val">{selected.category}</span>
                    </div>
                  </div>

                  {/* Description */}
                  {selected.description && (
                    <div className="tm-panel-section">
                      <h3 className="tm-panel-section-title">Description</h3>
                      <p className="tm-panel-desc">{selected.description}</p>
                    </div>
                  )}

                  {/* Entry Request */}
                  {liveSelected?.entry_request_status && (
                    <div className="tm-panel-section">
                      <h3 className="tm-panel-section-title">
                        <FaKey style={{ marginRight: 6, fontSize: 13 }} />
                        Entry Request
                      </h3>
                      {liveSelected.entry_request_status === 'Approved' && (
                        <div className="tm-er-status tm-er-accepted">
                          <FaCheckCircle /> You approved entry access
                        </div>
                      )}
                      {liveSelected.entry_request_status === 'Denied' && (
                        <div className="tm-er-status tm-er-declined">
                          <FaTimes /> You declined entry access
                        </div>
                      )}
                      {liveSelected.entry_request_status === 'Pending' && (
                        <>
                          <p className="tm-er-pending-msg">
                            Your property manager is requesting access to your unit without you present.
                          </p>
                          <div className="tm-er-actions">
                            <button
                              className="tm-er-decline-btn"
                              onClick={() => handleEntryResponse('Denied')}
                              disabled={entryResponding}
                            >
                              <FaTimes style={{ marginRight: 5 }} /> Decline
                            </button>
                            <button
                              className="tm-er-accept-btn"
                              onClick={() => handleEntryResponse('Approved')}
                              disabled={entryResponding}
                            >
                              <FaCheckCircle style={{ marginRight: 5 }} /> Accept
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Photos */}
                  {(photosLoading || woPhotos.length > 0) && (
                    <div className="tm-panel-section">
                      <h3 className="tm-panel-section-title">
                        <FaImages style={{ marginRight: 6, fontSize: 13 }} />
                        Photos
                      </h3>
                      {photosLoading ? (
                        <p className="tm-panel-empty">Loading photos…</p>
                      ) : (
                        <div className="tm-photos-grid">
                          {woPhotos.map(photo => (
                            <div key={photo.photo_id} className="tm-photo-wrap" onClick={() => setLightboxPhoto(photo)}>
                              <img src={photo.blobUrl} alt="work order" className="tm-photo-thumb" />
                              <div className="tm-photo-expand"><FaExpand /></div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Assigned Vendors */}
                  {woVendors.length > 0 && (
                    <div className="tm-panel-section">
                      <h3 className="tm-panel-section-title">
                        <FaTools style={{ marginRight: 6, fontSize: 13 }} />
                        Assigned Vendors
                      </h3>
                      <div className="tm-vendor-list">
                        {woVendors.map((v, i) => (
                          <div key={i} className="tm-vendor-row">
                            <div className="tm-vendor-trade">{v.Trade || '—'}</div>
                            <div className="tm-vendor-name">{v.Full_Name || '—'}</div>
                            {v.Phone_Number && (
                              <div className="tm-vendor-phone">
                                <FaPhone style={{ fontSize: 10, marginRight: 4 }} />{v.Phone_Number}
                              </div>
                            )}
                            {v.Notes && <div className="tm-vendor-notes">{v.Notes}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Timeline */}
                  <div className="tm-panel-section">
                    <h3 className="tm-panel-section-title">Timeline</h3>
                    {timelineLoading ? (
                      <p className="tm-panel-empty">Loading…</p>
                    ) : timeline.length === 0 ? (
                      <p className="tm-panel-empty">No timeline events yet.</p>
                    ) : (
                      <div className="tm-panel-timeline">
                        {[...timeline].reverse().map((event, i, arr) => (
                          <div key={i} className="tm-tl-item">
                            <div className="tm-tl-dot-wrap">
                              <div className={`tm-tl-dot ${i === 0 ? 'tm-tl-dot-first' : ''}`}>
                                {i === 0 ? <FaClock /> : <FaCalendarAlt />}
                              </div>
                              {i < arr.length - 1 && <div className="tm-tl-line" />}
                            </div>
                            <div className="tm-tl-content">
                              <span className="tm-tl-title">
                                {event.Event || event.event || event.Title || event.title || 'Update'}
                              </span>
                              <span className="tm-tl-date">
                                {fmtDateTime(event.Date || event.date || event.Created_At || event.created_at)}
                                {(event.Updated_By || event.updated_by) && (() => {
                                  const by = event.Updated_By || event.updated_by || '';
                                  const name = event.Updated_By_Name || event.Full_Name || event.Name ||
                                    (by === userEmail ? tenantName : by === pmEmail ? pmName : by);
                                  return <> · {name}</>;
                                })()}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                </div>

                {/* Footer */}
                {liveSelected?.status?.toLowerCase() !== 'completed' && (
                <div className="tm-panel-footer">
                  {liveSelected?.close_requested ? (
                    <div className="tm-close-requested-msg">
                      <FaCheckCircle style={{ marginRight: 6, color: '#15803d' }} />
                      Close request sent to your property manager
                    </div>
                  ) : (
                    <button
                      className="tm-cancel-btn"
                      onClick={handleRequestClose}
                      disabled={closeRequesting}
                    >
                      <FaTimes style={{ fontSize: 13 }} />
                      {closeRequesting ? 'Sending…' : 'Request to Close'}
                    </button>
                  )}
                </div>
                )}
              </>
            );
          })()}
        </div>

        {/* ── New request modal (3 steps) ── */}
        {showNew && (
          <div className="tm-overlay" onClick={closeNew}>
            <div className="tm-modal tm-modal-new" onClick={e => e.stopPropagation()}>

              {/* Step header */}
              <div className="tm-step-hd">
                <div className="tm-step-hd-top">
                  <div>
                    <p className="tm-step-label">STEP {step} OF 3</p>
                    <h2 className="tm-step-title">{STEP_LABELS[step]}</h2>
                  </div>
                  <button className="tm-modal-close" onClick={closeNew}><FaTimes /></button>
                </div>
                <div className="tm-progress-track">
                  <div className="tm-progress-fill" style={{ width: `${(step / 3) * 100}%` }} />
                </div>
              </div>

              {/* ── Step 1: Category grid ── */}
              {step === 1 && (
                <div className="tm-modal-body-scroll">
                  <div className="tm-cat-grid">
                    {CATEGORIES.map(({ id, Icon, color }) => (
                      <button
                        key={id}
                        className={`tm-cat-btn ${newWO.category === id ? 'tm-cat-selected' : ''}`}
                        onClick={() => { setNewWO(p => ({ ...p, category: id })); setStep(2); }}
                      >
                        <Icon style={{ fontSize: 28, color }} />
                        <span>{id}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Step 2: Title + description ── */}
              {step === 2 && (
                <>
                  <div className="tm-modal-body-scroll">
                    <div className="tm-new-form">
                      <label className="tm-field-lbl">TITLE <span style={{ color: '#ef4444' }}>*</span></label>
                      <input
                        className="tm-input"
                        placeholder="Brief summary of the issue"
                        value={newWO.title}
                        onChange={e => setNewWO(p => ({ ...p, title: e.target.value }))}
                        autoFocus
                      />
                      <div className="tm-desc-row">
                        <label className="tm-field-lbl">DESCRIPTION <span style={{ color: '#ef4444' }}>*</span></label>
                        <span className="tm-char-count">{newWO.description.length}/250</span>
                      </div>
                      <textarea
                        className="tm-input tm-textarea"
                        placeholder="Tell us more — when did it start, how bad is it?"
                        maxLength={250}
                        value={newWO.description}
                        onChange={e => setNewWO(p => ({ ...p, description: e.target.value }))}
                      />

                      <label className="tm-field-lbl" style={{ marginTop: 12 }}>PHOTOS <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
                      <div
                        className="tm-photo-upload-area"
                        onClick={() => newWoPhotoInputRef.current?.click()}
                      >
                        <FaImages style={{ fontSize: 22, color: '#9ca3af', marginBottom: 4 }} />
                        <span className="tm-photo-upload-text">Tap to add photos</span>
                      </div>
                      <input
                        ref={newWoPhotoInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        style={{ display: 'none' }}
                        onChange={e => {
                          const files = Array.from(e.target.files || []);
                          setNewWoPhotos(prev => [...prev, ...files]);
                          e.target.value = '';
                        }}
                      />
                      {newWoPhotos.length > 0 && (
                        <div className="tm-photo-preview-row">
                          {newWoPhotos.map((file, i) => (
                            <div key={i} className="tm-photo-preview-wrap">
                              <img src={URL.createObjectURL(file)} alt="" className="tm-photo-preview-img" />
                              <button
                                className="tm-photo-preview-remove"
                                onClick={() => setNewWoPhotos(prev => prev.filter((_, j) => j !== i))}
                              >×</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="tm-modal-footer tm-step-footer">
                    <button className="tm-back-btn" onClick={() => setStep(1)}>Back</button>
                    <button
                      className="tm-continue-btn"
                      onClick={() => setStep(3)}
                      disabled={!newWO.title.trim()}
                    >Continue</button>
                  </div>
                </>
              )}

              {/* ── Step 3: Urgency + toggle + summary ── */}
              {step === 3 && (
                <>
                  <div className="tm-modal-body-scroll">

                    <label className="tm-field-lbl">URGENCY</label>
                    <div className="tm-urgency-grid">
                      {URGENCIES.map(({ id, sub, Icon }) => (
                        <button
                          key={id}
                          className={`tm-urgency-btn ${newWO.urgency === id ? 'tm-urgency-selected' : ''}`}
                          onClick={() => setNewWO(p => ({ ...p, urgency: id }))}
                        >
                          <Icon style={{ fontSize: 18, marginBottom: 6, color: newWO.urgency === id ? '#2563eb' : '#aaa' }} />
                          <span className="tm-urgency-label">{id}</span>
                          <span className="tm-urgency-sub">{sub}</span>
                        </button>
                      ))}
                    </div>

                    <div className="tm-enter-row">
                      <div className="tm-enter-info">
                        <span className="tm-enter-title">OK to enter if I'm not home</span>
                        <span className="tm-enter-sub">Allow your landlord or a technician to access your unit without you present.</span>
                      </div>
                      <label className="tm-toggle">
                        <input
                          type="checkbox"
                          checked={newWO.okToEnter}
                          onChange={e => setNewWO(p => ({ ...p, okToEnter: e.target.checked }))}
                        />
                        <span className="tm-toggle-track" />
                      </label>
                    </div>

                    <label className="tm-field-lbl" style={{ marginTop: 4 }}>SUMMARY</label>
                    <div className="tm-summary-card">
                      <div className="tm-summary-icon">
                        {selectedCat && <selectedCat.Icon style={{ fontSize: 20, color: selectedCat.color }} />}
                      </div>
                      <div className="tm-summary-info">
                        <span className="tm-summary-title">{newWO.title || '—'}</span>
                        <span className="tm-summary-cat">{newWO.category}</span>
                      </div>
                      <span className={`tm-badge tm-pri-badge ${PRI_CLS[newWO.urgency] || 'tm-pri-medium'}`}>
                        {newWO.urgency} priority
                      </span>
                    </div>

                  </div>
                  <div className="tm-modal-footer tm-step-footer">
                    <button className="tm-back-btn" onClick={() => setStep(2)}>Back</button>
                    <button
                      className="tm-submit-btn"
                      onClick={handleSubmitNew}
                      disabled={submitting}
                    >{submitting ? 'Submitting...' : 'Submit Request'}</button>
                  </div>
                </>
              )}

            </div>
          </div>
        )}

      </div>

      {/* Photo lightbox */}
      {lightboxPhoto && (
        <div className="tm-lightbox" onClick={() => setLightboxPhoto(null)}>
          <button className="tm-lightbox-close" onClick={() => setLightboxPhoto(null)}><FaTimes /></button>
          <img src={lightboxPhoto.blobUrl} alt="work order" className="tm-lightbox-img" onClick={e => e.stopPropagation()} />
        </div>
      )}

      <Tutorial
        steps={TM_STEPS}
        storageKey={`hasSeenTenantMaintenanceTutorial_${userEmail}`}
      />
      {confirmDialog && (
        <ConfirmModal
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmLabel={confirmDialog.confirmLabel}
          variant={confirmDialog.variant || 'warning'}
          onConfirm={() => { confirmDialog.onConfirm(); setConfirmDialog(null); }}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
    </div>
  );
}

export default TenantMaintenance;
